import { useMemo } from 'react';
import { useMessage } from '../components/MessageProvider';
import {
  type ApiCallOptions,
  type ApiResult,
  type CreateDocumentRequest,
  type CreateDocumentResponse,
  type CreateFreeAccountResponse,
  type DeleteDocumentResponse,
  type GetDocumentsRequest,
  type GetDocumentsResponse,
  type LoadDocumentRequest,
  type LoadDocumentResponse,
  type SaveDocumentRequest,
  type SaveDocumentResponse,
  type GetTAccountsRequest,
  type GetTAccountsResponse,
  type CreateTAccountRequest,
  type TAccountRecord,
  type UpdateTAccountRequest,
  type DeleteTAccountResponse,
  type SendFeedbackRequest,
  type SendFeedbackResponse,
  type UserSettingsRequest,
  type SaveUserSettingsRequest,
  type GetUserSettingsResponse,
  type SaveUserSettingsResponse,
  type GetRuntimeFlagsResponse,
  type BillingPlansRequest,
  type BillingPlansResponse,
  type BillingStatusRequest,
  type BillingStatusResponse,
  type BillingDiagnosticsResponse,
  type GetAdminPlansResponse,
  type UpdateAdminPlansRequest,
  type UpdateAdminPlansResponse,
  type GetAdminUsersResponse,
  type DeleteAdminUserResponse,
  type CancelBillingSubscriptionRequest,
  type CancelBillingSubscriptionResponse,
  type CreateBillingCheckoutSessionRequest,
  type CreateBillingCheckoutSessionResponse,
  type ValidateDiscountCodeResponse,
  type AdminDiscountCodeRequest,
  type AdminDiscountCodesResponse,
  type AdminCommissionsResponse,
  type AdminCommissionPaymentBatch,
  type UpdateRuntimeFlagsRequest,
  type UpdateRuntimeFlagsResponse,
  createFreeAccount as createFreeAccountCore,
  createDocument as createDocumentCore,
  getDocuments as getDocumentsCore,
  loadDocument as loadDocumentCore,
  deleteDocument as deleteDocumentCore,
  saveDocument as saveDocumentCore,
  getTAccounts as getTAccountsCore,
  createTAccount as createTAccountCore,
  updateTAccount as updateTAccountCore,
  deleteTAccount as deleteTAccountCore,
  sendFeedback as sendFeedbackCore,
  getUserSettings as getUserSettingsCore,
  saveUserSettings as saveUserSettingsCore,
  getRuntimeFlags as getRuntimeFlagsCore,
  getBillingPlans as getBillingPlansCore,
  getBillingStatus as getBillingStatusCore,
  getBillingDiagnostics as getBillingDiagnosticsCore,
  getAdminPlans as getAdminPlansCore,
  createBillingCheckoutSession as createBillingCheckoutSessionCore,
  cancelBillingSubscription as cancelBillingSubscriptionCore,
  updateAdminPlans as updateAdminPlansCore,
  getAdminUsers as getAdminUsersCore,
  deleteAdminUser as deleteAdminUserCore,
  updateRuntimeFlags as updateRuntimeFlagsCore,
  validateDiscountCode as validateDiscountCodeCore,
  getAdminDiscountCodes as getAdminDiscountCodesCore,
  createAdminDiscountCode as createAdminDiscountCodeCore,
  updateAdminDiscountCode as updateAdminDiscountCodeCore,
  deleteAdminDiscountCode as deleteAdminDiscountCodeCore,
  getAdminCommissions as getAdminCommissionsCore,
  settleAdminCommissions as settleAdminCommissionsCore,
} from '../lib/apiClient';

export type UseApi = {
  createFreeAccount: (
    options?: Omit<ApiCallOptions, 'showError'>,
  ) => Promise<ApiResult<CreateFreeAccountResponse>>;
  createDocument: (
    request: CreateDocumentRequest,
    options?: Omit<ApiCallOptions, 'showError'>,
  ) => Promise<ApiResult<CreateDocumentResponse>>;
  saveDocument: (
    request: SaveDocumentRequest,
    options?: Omit<ApiCallOptions, 'showError'>,
  ) => Promise<ApiResult<SaveDocumentResponse>>;
  getDocuments: (
    request: GetDocumentsRequest,
    options?: Omit<ApiCallOptions, 'showError'>,
  ) => Promise<ApiResult<GetDocumentsResponse>>;
  loadDocument: (
    request: LoadDocumentRequest,
    options?: Omit<ApiCallOptions, 'showError'>,
  ) => Promise<ApiResult<LoadDocumentResponse>>;
  deleteDocument: (
    documentId: string,
    options?: Omit<ApiCallOptions, 'showError'>,
  ) => Promise<ApiResult<DeleteDocumentResponse>>;
  getTAccounts: (
    request: GetTAccountsRequest,
    options?: Omit<ApiCallOptions, 'showError'>,
  ) => Promise<ApiResult<GetTAccountsResponse>>;
  createTAccount: (
    request: CreateTAccountRequest,
    options?: Omit<ApiCallOptions, 'showError'>,
  ) => Promise<ApiResult<TAccountRecord>>;
  updateTAccount: (
    request: UpdateTAccountRequest,
    options?: Omit<ApiCallOptions, 'showError'>,
  ) => Promise<ApiResult<TAccountRecord>>;
  deleteTAccount: (
    taccountId: string,
    accountId: string,
    options?: Omit<ApiCallOptions, 'showError'>,
  ) => Promise<ApiResult<DeleteTAccountResponse>>;
  sendFeedback: (
    request: SendFeedbackRequest,
    options?: Omit<ApiCallOptions, 'showError'>,
  ) => Promise<ApiResult<SendFeedbackResponse>>;
  getUserSettings: (
    request?: UserSettingsRequest,
    options?: Omit<ApiCallOptions, 'showError'>,
  ) => Promise<ApiResult<GetUserSettingsResponse>>;
  saveUserSettings: (
    request: SaveUserSettingsRequest,
    options?: Omit<ApiCallOptions, 'showError'>,
  ) => Promise<ApiResult<SaveUserSettingsResponse>>;
  getRuntimeFlags: (
    options?: Omit<ApiCallOptions, 'showError'>,
  ) => Promise<ApiResult<GetRuntimeFlagsResponse>>;
  updateRuntimeFlags: (
    request: UpdateRuntimeFlagsRequest,
    options?: Omit<ApiCallOptions, 'showError'>,
  ) => Promise<ApiResult<UpdateRuntimeFlagsResponse>>;
  getBillingPlans: (
    request?: BillingPlansRequest,
    options?: Omit<ApiCallOptions, 'showError'>,
  ) => Promise<ApiResult<BillingPlansResponse>>;
  getBillingStatus: (
    request?: BillingStatusRequest,
    options?: Omit<ApiCallOptions, 'showError'>,
  ) => Promise<ApiResult<BillingStatusResponse>>;
  createBillingCheckoutSession: (
    request: CreateBillingCheckoutSessionRequest,
    options?: Omit<ApiCallOptions, 'showError'>,
  ) => Promise<ApiResult<CreateBillingCheckoutSessionResponse>>;
  validateDiscountCode: (
    code: string,
    options?: Omit<ApiCallOptions, 'showError'>,
  ) => Promise<ApiResult<ValidateDiscountCodeResponse>>;
  cancelBillingSubscription: (
    request?: CancelBillingSubscriptionRequest,
    options?: Omit<ApiCallOptions, 'showError'>,
  ) => Promise<ApiResult<CancelBillingSubscriptionResponse>>;
  getBillingDiagnostics: (
    options?: Omit<ApiCallOptions, 'showError'>,
  ) => Promise<ApiResult<BillingDiagnosticsResponse>>;
  getAdminPlans: (
    options?: Omit<ApiCallOptions, 'showError'>,
  ) => Promise<ApiResult<GetAdminPlansResponse>>;
  updateAdminPlans: (
    request: UpdateAdminPlansRequest,
    options?: Omit<ApiCallOptions, 'showError'>,
  ) => Promise<ApiResult<UpdateAdminPlansResponse>>;
  getAdminUsers: (
    page: number,
    exportAll?: boolean,
    showUnnamed?: boolean,
    options?: Omit<ApiCallOptions, 'showError'>,
  ) => Promise<ApiResult<GetAdminUsersResponse>>;
  deleteAdminUser: (
    userId: string,
    options?: Omit<ApiCallOptions, 'showError'>,
  ) => Promise<ApiResult<DeleteAdminUserResponse>>;
  getAdminDiscountCodes: (
    options?: Omit<ApiCallOptions, 'showError'>,
  ) => Promise<ApiResult<AdminDiscountCodesResponse>>;
  createAdminDiscountCode: (
    request: AdminDiscountCodeRequest,
    options?: Omit<ApiCallOptions, 'showError'>,
  ) => Promise<ApiResult<{ discount_code_id: number }>>;
  updateAdminDiscountCode: (
    codeId: number,
    request: AdminDiscountCodeRequest,
    options?: Omit<ApiCallOptions, 'showError'>,
  ) => Promise<ApiResult<{ discount_code_id: number; expired_checkout_sessions: number }>>;
  deleteAdminDiscountCode: (
    codeId: number,
    options?: Omit<ApiCallOptions, 'showError'>,
  ) => Promise<ApiResult<{ discount_code_id: number; expired_checkout_sessions: number }>>;
  getAdminCommissions: (
    options?: Omit<ApiCallOptions, 'showError'>,
  ) => Promise<ApiResult<AdminCommissionsResponse>>;
  settleAdminCommissions: (
    contentProviderId: number,
    unpaidCommissionIds: number[],
    options?: Omit<ApiCallOptions, 'showError'>,
  ) => Promise<ApiResult<AdminCommissionPaymentBatch>>;
};

export function useApi(): UseApi {
  const { showError } = useMessage();

  return useMemo(
    () => ({
      createFreeAccount: (options = {}) =>
        createFreeAccountCore({ ...options, showError }),
      createDocument: (request, options = {}) =>
        createDocumentCore(request, { ...options, showError }),
      saveDocument: (request, options = {}) =>
        saveDocumentCore(request, { ...options, showError }),
      getDocuments: (request, options = {}) =>
        getDocumentsCore(request, { ...options, showError }),
      loadDocument: (request, options = {}) =>
        loadDocumentCore(request, { ...options, showError }),
      deleteDocument: (documentId, options = {}) =>
        deleteDocumentCore(documentId, { ...options, showError }),
      getTAccounts: (request, options = {}) =>
        getTAccountsCore(request, { ...options, showError }),
      createTAccount: (request, options = {}) =>
        createTAccountCore(request, { ...options, showError }),
      updateTAccount: (request, options = {}) =>
        updateTAccountCore(request, { ...options, showError }),
      deleteTAccount: (taccountId, accountId, options = {}) =>
        deleteTAccountCore(taccountId, accountId, { ...options, showError }),
      sendFeedback: (request, options = {}) =>
        sendFeedbackCore(request, { ...options, showError }),
      getUserSettings: (request = {}, options = {}) =>
        getUserSettingsCore(request, { ...options, showError }),
      saveUserSettings: (request, options = {}) =>
        saveUserSettingsCore(request, { ...options, showError }),
      getRuntimeFlags: (options = {}) =>
        getRuntimeFlagsCore({ ...options, showError }),
      getBillingPlans: (request = {}, options = {}) =>
        getBillingPlansCore(request, { ...options, showError }),
      getBillingStatus: (request = {}, options = {}) =>
        getBillingStatusCore(request, { ...options, showError }),
      createBillingCheckoutSession: (request, options = {}) =>
        createBillingCheckoutSessionCore(request, { ...options, showError }),
      validateDiscountCode: (code, options = {}) =>
        validateDiscountCodeCore(code, { ...options, showError }),
      cancelBillingSubscription: (request = {}, options = {}) =>
        cancelBillingSubscriptionCore(request, { ...options, showError }),
      getBillingDiagnostics: (options = {}) =>
        getBillingDiagnosticsCore({ ...options, showError }),
      getAdminPlans: (options = {}) =>
        getAdminPlansCore({ ...options, showError }),
      updateAdminPlans: (request, options = {}) =>
        updateAdminPlansCore(request, { ...options, showError }),
      getAdminUsers: (page, exportAll = false, showUnnamed = false, options = {}) =>
        getAdminUsersCore(page, exportAll, showUnnamed, { ...options, showError }),
      deleteAdminUser: (userId, options = {}) =>
        deleteAdminUserCore(userId, { ...options, showError }),
      getAdminDiscountCodes: (options = {}) =>
        getAdminDiscountCodesCore({ ...options, showError }),
      createAdminDiscountCode: (request, options = {}) =>
        createAdminDiscountCodeCore(request, { ...options, showError }),
      updateAdminDiscountCode: (codeId, request, options = {}) =>
        updateAdminDiscountCodeCore(codeId, request, { ...options, showError }),
      deleteAdminDiscountCode: (codeId, options = {}) =>
        deleteAdminDiscountCodeCore(codeId, { ...options, showError }),
      getAdminCommissions: (options = {}) =>
        getAdminCommissionsCore({ ...options, showError }),
      settleAdminCommissions: (providerId, commissionIds, options = {}) =>
        settleAdminCommissionsCore(providerId, commissionIds, { ...options, showError }),
      updateRuntimeFlags: (request, options = {}) =>
        updateRuntimeFlagsCore(request, { ...options, showError }),
    }),
    [showError]
  );
}
